import { Backdrop, Button, InputText } from '@/components/atoms';
import { useCallback, useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { HOOKS } from '@/utils/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MdFilterList } from 'react-icons/md';
import { useSelector } from 'react-redux';
import ModalCategory from './ModalCategory';
import CatalogSourceFilter from '@/components/molecules/CatalogSourceFilter';

const hideScrollbarStyle = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const CatalogFilter = ({
  productType,
  eventKind,
  priceMin,
  priceMax,
  categoryIds,
  tenantOnly,
  onChangePriceMin,
  onChangePriceMax,
  onChangeProductType,
  onChangeEventKind,
  onChangeCategory,
  onReset,
  onChangeTenant,
  showCatalogFilterMobile,
  setShowCatalogFilterMobile,
}) => {
  const { t } = useTranslation();

  const setting = useSelector((state) => state.client.payload?.available_order);
  const showPrice = setting?.includes('BUY_COURSE');

  const categories = useSelector((state) => state.event?.categories?.payload?.categories || []);
  const eventTypes = useSelector((state) => state.event?.categories?.payload?.types || []);
  const eventKinds = useSelector((state) => state.event?.categories?.payload?.kinds || []);

  const [selectedCategory, setSelectedCategory] = useState([]);
  const [showMoreCategory, setShowMoreCategory] = useState(false);
  const [showFilter, setShowFilter] = useState({ category: true, type: true, price: true, kind: true });

  const swipeHandlers = HOOKS.useSwipeClose(() => setShowCatalogFilterMobile(false), 70);

  const LIMIT_START_PRICE = 0;
  const LIMIT_END_PRICE = 20000000;

  const catalogFilterMobileContainerClasses = classNames({
    'visible rounded-t-lg opacity-100': showCatalogFilterMobile,
    'invisible opacity-0 lg:visible lg:opacity-100': !showCatalogFilterMobile,
  });

  const catalogFilterMobileClasses = classNames({
    'translate-y-0': showCatalogFilterMobile,
    'translate-y-full lg:translate-y-0': !showCatalogFilterMobile,
  });

  const [localPriceMin, setLocalPriceMin] = useState(priceMin);
  const [localPriceMax, setLocalPriceMax] = useState(priceMax);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedPriceMinChange = useCallback(
    debounce((value) => {
      onChangePriceMin(value);
    }, 500),
    [onChangePriceMin]
  );

  const debouncedPriceMaxChange = useCallback(
    debounce((value) => {
      onChangePriceMax(value);
    }, 500),
    [onChangePriceMax]
  );

  useEffect(() => {
    setLocalPriceMin(priceMin);
  }, [priceMin]);

  useEffect(() => {
    setLocalPriceMax(priceMax);
  }, [priceMax]);

  const handlePriceMinChange = (e) => {
    const { value } = e.target;
    setLocalPriceMin(value);
    debouncedPriceMinChange(value);
  };

  const handlePriceMaxChange = (e) => {
    const { value } = e.target;
    setLocalPriceMax(value);
    debouncedPriceMaxChange(value);
  };

  const getTopCategories = () => {
    return categories.slice(0, 3).map((category) => ({
      id: category.id,
      title: category.title,
      subcategories: category.subcategories || [],
      hasSubcategories: Boolean(category.subcategories && category.subcategories.length > 0),
    }));
  };

  const getSubcategories = () => {
    const subcats = [];
    categories.forEach((category) => {
      if (category.subcategories && category.subcategories.length > 0) {
        category.subcategories.forEach((subcat) => {
          subcats.push({
            id: subcat.id,
            name: subcat.title,
            parentId: category.id,
            parentTitle: category.title,
          });
        });
      }
    });
    return subcats;
  };

  const handleChangeCategory = (categoryId, e) => {
    if (!categoryId) return;

    const cat = Array.isArray(selectedCategory) ? [...selectedCategory] : [];

    if (e.target.checked) {
      cat.push(categoryId.toString());
    } else {
      const findIndex = cat.findIndex((id) => id === categoryId.toString());
      if (findIndex !== -1) {
        cat.splice(findIndex, 1);
      }
    }

    onChangeCategory(cat.join(','));
  };

  const toggleMoreCategory = () => {
    setShowMoreCategory(!showMoreCategory);
  };

  useEffect(() => {
    if (categoryIds) {
      setSelectedCategory(categoryIds.split(','));
    } else {
      setSelectedCategory([]);
    }
  }, [categoryIds]);

  const getCategoryTitle = (categoryId) => {
    const mainCategory = categories.find((category) => category.id.toString() === categoryId);
    if (mainCategory) return mainCategory.title;

    const subcategory = categories
      .flatMap((category) => category.subcategories || [])
      .find((subcat) => subcat.id.toString() === categoryId);

    return subcategory ? subcategory.title : '';
  };

  return (
    <>
      {/* Add the style tag */}
      <style>{hideScrollbarStyle}</style>

      <div
        className={`fixed w-full h-screen lg:h-auto lg:relative lg:w-full left-0 bottom-0 z-50 lg:z-10 ${catalogFilterMobileContainerClasses}`}
      >
        <div className="lg:hidden">
          <Backdrop active={showCatalogFilterMobile} onClick={() => setShowCatalogFilterMobile(false)} />
        </div>

        <div
          className={`bg-white absolute bottom-0 lg:relative shadow-sm transition-all rounded-t-3xl lg:rounded-md transform w-full left-0 duration-300 flex flex-col max-h-[85vh] lg:max-h-none ${catalogFilterMobileClasses}`}
        >
          <ModalCategory
            show={showMoreCategory}
            subcategories={getSubcategories()}
            selectedCategory={selectedCategory}
            onChangeCategory={(val) => {
              onChangeCategory(val);
              setShowMoreCategory(false);
            }}
            onClose={() => setShowMoreCategory(false)}
            onReset={onReset}
          />

          <div
            className="flex items-center rounded-t-3xl justify-between py-11 px-6 md:p-6 pb-3 border-b sticky top-0 bg-white z-10"
            {...swipeHandlers}
          >
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-300 rounded-full lg:hidden" />
            <div className="flex items-center space-x-3">
              <MdFilterList size={16} />
              <span className="text-sm">Filter</span>
            </div>
            <div className="flex items-center">
              <button className="mr-6 lg:mr-0 text-secondary text-sm" type="button" onClick={onReset}>
                Reset
              </button>
              {/* <button
                className="block lg:hidden bg-gray-100 rounded-full p-3"
                onClick={() => setShowCatalogFilterMobile(false)}
                type="button"
              >
                <FiX size={20} />
              </button> */}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-12 hide-scrollbar">
            <form>
              <div className="border-b py-5">
                <div className="mb-3 flex justify-between">
                  <div className="text-sm font-semibold">Kategori</div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowFilter({ ...showFilter, category: !showFilter.category })}
                    >
                      {showFilter.category ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  </div>
                </div>
                <div className={`${showFilter.category ? 'static' : 'hidden'}`}>
                  <div className="grid grid-cols-1 gap-4">
                    {getTopCategories().map((category) => (
                      <div className="pb-1" key={category.id}>
                        <div className="font-medium mb-2 text-gray-500">{category.title}</div>

                        {category.hasSubcategories && (
                          <div className="ml-1 space-y-3">
                            {category.subcategories.slice(0, 5).map((subcat) => (
                              <div
                                key={subcat.id}
                                className="cursor-pointer flex items-center"
                                onClick={() =>
                                  handleChangeCategory(subcat.id, {
                                    target: { checked: !selectedCategory.includes(subcat.id.toString()) },
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    handleChangeCategory(subcat.id, {
                                      target: { checked: !selectedCategory.includes(subcat.id.toString()) },
                                    });
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                <div
                                  className={`w-6 h-6 rounded-md border-2 mr-2 flex items-center justify-center ${
                                    selectedCategory.includes(subcat.id.toString())
                                      ? 'bg-green-500 border-green-500'
                                      : 'border-gray-400'
                                  }`}
                                >
                                  {selectedCategory.includes(subcat.id.toString()) && (
                                    <div className="text-white">✓</div>
                                  )}
                                </div>
                                <span className="text-sm">{subcat.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={toggleMoreCategory}
                      style={{ padding: '0' }}
                      className="text-green-500 text-sm hover:text-green-600"
                    >
                      Lihat Selengkapnya
                    </button>
                  </div>
                </div>
              </div>

              <CatalogSourceFilter selectedTenant={tenantOnly} onChangeTenant={onChangeTenant} />

              <div className="border-b py-5">
                <div className="mb-3 flex justify-between">
                  <div className="text-sm font-semibold">Jenis Event</div>
                  <button type="button" onClick={() => setShowFilter({ ...showFilter, kind: !showFilter.kind })}>
                    {showFilter.kind ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`${showFilter.kind ? 'static' : 'hidden'}`}>
                  <div className="grid grid-cols-1 gap-2 md:flex lg:flex-col md:space-x-3 lg:space-x-0">
                    {eventKinds?.map((kind) => {
                      const isSelected = eventKind ? eventKind.split(',').includes(kind.id.toString()) : false;
                      return (
                        <div
                          key={kind.id}
                          className="rounded-md p-2 capitalize w-full cursor-pointer flex items-center"
                          onClick={() => {
                            const currentKinds = eventKind ? eventKind.split(',') : [];
                            let newKinds;

                            if (isSelected) {
                              newKinds = currentKinds.filter((id) => id !== kind.id.toString());
                            } else {
                              newKinds = [...currentKinds, kind.id.toString()];
                            }

                            onChangeEventKind(newKinds.join(','));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              const currentKinds = eventKind ? eventKind.split(',') : [];
                              let newKinds;

                              if (isSelected) {
                                newKinds = currentKinds.filter((id) => id !== kind.id.toString());
                              } else {
                                newKinds = [...currentKinds, kind.id.toString()];
                              }

                              onChangeEventKind(newKinds.join(','));
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`w-6 h-6 rounded-md border-2 mr-2 flex items-center justify-center ${
                              isSelected ? 'bg-green-500 border-green-500' : 'border-gray-400'
                            }`}
                          >
                            {isSelected && <div className="text-white">✓</div>}
                          </div>
                          <span className="text-sm">{kind.title}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="border-b py-5">
                <div className="mb-3 flex justify-between">
                  <div className="text-sm font-semibold">Tipe Event</div>
                  <button type="button" onClick={() => setShowFilter({ ...showFilter, type: !showFilter.type })}>
                    {showFilter.type ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`${showFilter.type ? 'static' : 'hidden'}`}>
                  <div className="grid grid-cols-1 gap-2 md:flex lg:flex-col md:space-x-3 lg:space-x-0">
                    {eventTypes?.map((type) => {
                      const isSelected = productType?.split(',').includes(type.id);
                      return (
                        <div
                          key={type.id}
                          className="rounded-md p-2 capitalize w-full cursor-pointer flex items-center"
                          onClick={() => {
                            const currentTypes = productType ? productType.split(',') : [];
                            let newTypes;

                            if (isSelected) {
                              newTypes = currentTypes.filter((id) => id !== type.id);
                            } else {
                              newTypes = [...currentTypes, type.id];
                            }

                            onChangeProductType(newTypes.join(','));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              const currentTypes = productType ? productType.split(',') : [];
                              let newTypes;

                              if (isSelected) {
                                newTypes = currentTypes.filter((id) => id !== type.id);
                              } else {
                                newTypes = [...currentTypes, type.id];
                              }

                              onChangeProductType(newTypes.join(','));
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div
                            className={`w-6 h-6 rounded-md border-2 mr-2 flex items-center justify-center ${
                              isSelected ? 'bg-green-500 border-green-500' : 'border-gray-400'
                            }`}
                          >
                            {isSelected && <div className="text-white">✓</div>}
                          </div>
                          <span className="text-sm">{type.title}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {showPrice && (
                <div className="pt-5">
                  <div className="mb-3 flex justify-between">
                    <div className="text-sm font-semibold">Rentang Harga</div>
                    <button
                      type="button"
                      onClick={() => setShowFilter({ ...showFilter, price: !showFilter.price })}
                    >
                      {showFilter.price ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  </div>
                  <div className={`${showFilter.price ? 'static' : 'hidden'}`}>
                    <div className="flex flex-col justify-center space-y-3">
                      <InputText
                        type="number"
                        min={LIMIT_START_PRICE}
                        max={priceMax}
                        value={localPriceMin}
                        icon={<span className="text-sm">Rp.</span>}
                        onChange={handlePriceMinChange}
                        placeholder={t('minimum_price')}
                      />
                      <InputText
                        type="number"
                        min={priceMin}
                        max={LIMIT_END_PRICE}
                        value={localPriceMax}
                        icon={<span className="text-sm">Rp.</span>}
                        onChange={handlePriceMaxChange}
                        placeholder={t('maximum_price')}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer with Apply button for mobile */}
          <div className="sticky bottom-0 left-0 w-full p-4 bg-white border-t shadow-md lg:hidden">
            <button
              type="button"
              onClick={() => setShowCatalogFilterMobile(false)}
              className="w-full py-3 px-4 bg-tertiary-600 text-white font-medium rounded-full hover:bg-tertiary-dark transition-colors"
            >
              Terapkan
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

CatalogFilter.propTypes = {
  productType: PropTypes.string,
  eventKind: PropTypes.string,
  priceMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  priceMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoryIds: PropTypes.string,
  tenantOnly: PropTypes.string,
  onChangePriceMin: PropTypes.func,
  onChangePriceMax: PropTypes.func,
  onChangeProductType: PropTypes.func,
  onChangeEventKind: PropTypes.func,
  onChangeCategory: PropTypes.func,
  onChangeTenant: PropTypes.func,
  onReset: PropTypes.func,
  showCatalogFilterMobile: PropTypes.bool,
  setShowCatalogFilterMobile: PropTypes.func,
};

CatalogFilter.defaultProps = {
  productType: '',
  eventKind: '',
  priceMin: '',
  priceMax: '',
  categoryIds: '',
  tenantOnly: 'true',
  onChangePriceMin: () => {},
  onChangePriceMax: () => {},
  onChangeProductType: () => {},
  onChangeEventKind: () => {},
  onChangeCategory: () => {},
  onChangeTenant: () => {},
  onReset: () => {},
  showCatalogFilterMobile: false,
  setShowCatalogFilterMobile: () => {},
};

export default CatalogFilter;
